import { STORIES_ADMIN_PRIVILEGE } from '@opengov/stories-shared/constants';
import { useQuery } from '@tanstack/react-query';

import { fetchSubjectPublicationStoryClaim } from '../../api/subjectClaimsApi';
import { useToast } from '../../components/Toast/context/useToast.context';
import { subjectClaims as subjectClaimsMsgs } from '../../../locale/stories.en.locale.json';

import { subjectClaimsQueryKeys } from './subjectClaimsQueryKeys';

export type UseSubjectPublicationStoryClaimParams = {
  publicationId: string;
  subjectId: string;
  storyId?: string;
  entityUuid: string;
  storiesAccessControlEnabled: boolean;
} | null;

export function useSubjectPublicationStoryClaim(params: UseSubjectPublicationStoryClaimParams) {
  const { toggleToast } = useToast();
  const msgs = subjectClaimsMsgs;

  return useQuery({
    queryKey: params
      ? subjectClaimsQueryKeys.publicationStoryClaim({
          publicationId: params.publicationId,
          subjectId: params.subjectId,
          storyId: params.storyId,
          entityUuid: params.entityUuid,
          storiesAccessControlEnabled: params.storiesAccessControlEnabled
        })
      : ([...subjectClaimsQueryKeys.all, 'publicationStoryClaim', 'disabled'] as const),
    queryFn: () => fetchSubjectPublicationStoryClaim({
      publicationId: params!.publicationId,
      subjectId: params!.subjectId,
      storyId: params!.storyId,
      entityUuid: params!.entityUuid,
      stories_access_control_enabled: params!.storiesAccessControlEnabled
    }),
    enabled: params !== null && !!params.publicationId && !!params.subjectId && !!params.entityUuid,
    onError: () => {
      toggleToast({
        title: msgs?.somethingWentWrongError ?? 'Something went wrong',
        body: msgs?.failedToFetchSubjectClaims ?? 'Failed to fetch subject claims',
        severity: 'error'
      });
    }
  });
}

export function useHasAdminPrivilegeForPublication(
  params: UseSubjectPublicationStoryClaimParams
): boolean {
  const { data } = useSubjectPublicationStoryClaim(params);
  const claims = data?.allClaims;
  return Array.isArray(claims) && claims.includes(STORIES_ADMIN_PRIVILEGE);
}
